/**
 * Data Grid Service
 *
 * Provides detailed data lists for the data grid page:
 * - Form submissions (find by requests)
 * - Calendar events (visiting bookings: new/done/pending)
 */

const FORM_API = '/api/google-sheet?type=form-submissions';
const CALENDAR_API = '/api/calendar-events';

/**
 * Load all form submissions (find by requests)
 */
export async function loadFormSubmissions() {
  try {
    const response = await fetch(FORM_API);
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.submissions) ? data.submissions : [];
    }
  } catch (error) {
    console.error('[DataGrid] Error loading form submissions:', error);
  }
  return [];
}

/**
 * Load all calendar events (visiting bookings)
 */
export async function loadCalendarEvents() {
  try {
    const response = await fetch(CALENDAR_API);
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.events) ? data.events : [];
    }
  } catch (error) {
    console.error('[DataGrid] Error loading calendar events:', error);
  }
  return [];
}

/**
 * Get unique districts from all data
 */
export async function getAvailableDistricts() {
  const [findBy, visiting] = await Promise.all([
    getFindByRequests(),
    getVisitingBookings('all'),
  ]);

  const districts = new Set();

  findBy.forEach((item) => {
    if (item.district && item.district !== 'N/A') {
      districts.add(item.district);
    }
  });

  visiting.forEach((item) => {
    if (item.district && item.district !== 'N/A') {
      districts.add(item.district);
    }
  });

  return Array.from(districts).sort();
}

/**
 * Get find by request list with filters
 */
export async function getFindByRequests({ district = 'all', startDate = null, endDate = null } = {}) {
  const submissions = await loadFormSubmissions();
  
  return submissions.map((submission) => ({
    id: submission.id || submission[0],
    timestamp: submission.timestamp || submission[0],
    name: submission.name || submission[1] || 'N/A',
    phone: submission.phone || submission[2] || 'N/A',
    propertyType: submission.propertyType || submission[3] || 'N/A',
    district: submission.district || submission[4] || 'N/A',
    amenities: submission.amenities || submission[5] || 'N/A',
    moveInDate: submission.moveInDate || submission[6] || 'N/A',
    note: submission.note || submission[7] || '',
  })).filter((item) => {
    if (district !== 'all' && item.district !== district) return false;
    // Filter by moveInDate (planned visiting date) instead of submission timestamp
    if (startDate || endDate) {
      // Handle ISO date format (YYYY-MM-DD) or full ISO string
      let itemDateValue = item.moveInDate;
      if (itemDateValue.includes('T')) {
        itemDateValue = itemDateValue.split('T')[0];
      }
      const itemDate = new Date(itemDateValue);
      
      if (startDate && itemDate < new Date(startDate)) return false;
      if (endDate && itemDate > new Date(endDate)) return false;
    }
    return true;
  });
}

/**
 * Get visiting bookings filtered by status, district, and date range
 */
export async function getVisitingBookings({ status = 'all', district = 'all', startDate = null, endDate = null } = {}) {
  const events = await loadCalendarEvents();
  
  return events
    .filter((event) => {
      if (status !== 'all') {
        if (status === 'new') {
          const isNew = !event.isFinalized && !event.isRevisit;
          if (!isNew) return false;
        }
        if (status === 'pending' && !event.isRevisit) return false;
        if (status === 'done' && !event.isFinalized) return false;
      }
      if (district !== 'all' && event.district !== district) return false;
      if (startDate || endDate) {
        const eventDate = new Date(event.date || event.createdAt);
        if (startDate && eventDate < new Date(startDate)) return false;
        if (endDate && eventDate > new Date(endDate)) return false;
      }
      return true;
    })
    .map((event) => ({
      id: event.id,
      userName: event.userName || 'N/A',
      phone: event.phone || 'N/A',
      district: event.district || 'N/A',
      propertyName: event.propertyName || 'N/A',
      date: event.date || 'N/A',
      createdAt: event.createdAt || event.date || new Date().toISOString(),
      note: event.note || '',
      isFinalized: event.isFinalized || false,
      isRevisit: event.isRevisit || false,
      source: event.source || 'calendar',
    }));
}

/**
 * Get statistics summary for data grid page
 */
export async function getDataGridStats() {
  const [findByRequests, visitingBookings] = await Promise.all([
    getFindByRequests(),
    getVisitingBookings('all'),
  ]);

  return {
    findByRequests: {
      total: findByRequests.length,
    },
    visiting: {
      new: visitingBookings.filter((e) => !e.isFinalized && !e.isRevisit).length,
      done: visitingBookings.filter((e) => e.isFinalized).length,
      pending: visitingBookings.filter((e) => e.isRevisit).length,
      total: visitingBookings.length,
    },
  };
}

const DataGridService = {
  loadFormSubmissions,
  loadCalendarEvents,
  getFindByRequests,
  getVisitingBookings,
  getDataGridStats,
};

export default DataGridService;